import { useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext.jsx'
import Icon from './Icon.jsx'
import Popover from './Popover.jsx'
import { useHoverMenu } from '../lib/useHoverMenu.js'

/**
 * The wall view, one click down from the nav.
 *
 * It is a page you GO to — a preview of the screen running by itself in the workshop — so
 * the nav is its home, not the account button. But it is not opened during a working day
 * the way the boards and the Directory are, so it does not earn a permanent place on the
 * bar either. A menu is the difference between those two things.
 *
 * Settings does NOT live here, and that is deliberate: under the avatar is where people
 * reach for it, and that convention is worth more than the tidier rule that every
 * destination belongs in the nav. So this holds one item today. Named "More" rather than
 * "Wall View" because what it is for is the things that come later — a second screen, an
 * export, a report — and a menu that has to be renamed the first time it grows is worse
 * than one that reads a little roomy now.
 */
export default function NavMore() {
  const { can } = useAuth()
  const anchorRef = useRef(null)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const menu = useHoverMenu()

  const onWall = pathname === '/wall-view' || pathname === '/whiteboard'
  const here = onWall

  const go = (path) => {
    menu.close()
    navigate(path)
  }

  return (
    <div
      className={`nav-more ${here ? 'on' : ''}`}
      ref={anchorRef}
      onMouseEnter={menu.openNow}
      onMouseLeave={menu.closeSoon}
    >
      <button
        type="button"
        className="nav-more-trigger"
        aria-haspopup="menu"
        aria-expanded={menu.open}
        onClick={menu.toggle}
      >
        <span>More</span>
        <Icon name="down" size={12} strokeWidth={2.4} />
      </button>

      <Popover
        open={menu.open}
        anchorRef={anchorRef}
        close={menu.close}
        className="filter-panel nav-more-menu"
        role="menu"
        label="More"
        align="left"
        onMouseEnter={menu.openNow}
        onMouseLeave={menu.closeSoon}
      >
        {/* Offered to the people who run the boards, the same gate it carried as a nav item
            and before that in the account menu. */}
        {can?.seeWhiteboard && (
          <button
            type="button"
            role="menuitem"
            aria-current={onWall ? 'page' : undefined}
            className={`ctx-item ${onWall ? 'active' : ''}`}
            onClick={() => go('/wall-view')}
          >
            {/* A bare glyph, not a colour tile. The tiles in the board picker carry each
                list's own colour, which is how a row there is recognised before it is read;
                the wall view has no colour of its own, and a 12px television inside a pale
                square is a rectangle inside a rectangle — it read as an empty box. */}
            <span className="ctx-item-icon">
              <Icon name="tv" size={14} />
            </span>
            <span>Wall View</span>
          </button>
        )}
      </Popover>
    </div>
  )
}
